import * as FC from "@fluentui/react-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "../../services/api";

const pad = (value) => String(value).padStart(2, "0");
const dateText = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const initialDates = () => {
    const now = new Date();
    return {
        fromDate: dateText(new Date(now.getFullYear(), now.getMonth(), 1)),
        toDate: dateText(now),
    };
};
const dayBoundaryUtc = (value, addDays = 0) => {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day + addDays).toISOString();
};
const formatTime = (value) => value ? new Date(value).toLocaleString() : "-";

const useStyles = FC.makeStyles({
    root: { display: "flex", flexDirection: "column", ...FC.shorthands.gap("14px"), ...FC.shorthands.padding("16px"), overflow: "auto" },
    filters: { display: "flex", flexWrap: "wrap", alignItems: "end", ...FC.shorthands.gap("12px"), ...FC.shorthands.padding("14px"), backgroundColor: FC.tokens.colorNeutralBackground2, ...FC.shorthands.borderRadius(FC.tokens.borderRadiusMedium) },
    field: { display: "flex", minWidth: "180px", flexDirection: "column", ...FC.shorthands.gap("4px") },
    input: { minHeight: "32px" },
    cards: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", ...FC.shorthands.gap("12px") },
    card: { ...FC.shorthands.padding("16px"), ...FC.shorthands.border("1px", "solid", FC.tokens.colorNeutralStroke2), ...FC.shorthands.borderRadius(FC.tokens.borderRadiusMedium) },
    value: { fontSize: "28px", fontWeight: 600, marginTop: "6px" },
    section: { ...FC.shorthands.padding("14px"), ...FC.shorthands.border("1px", "solid", FC.tokens.colorNeutralStroke2), ...FC.shorthands.borderRadius(FC.tokens.borderRadiusMedium), backgroundColor: FC.tokens.colorNeutralBackground1 },
    sectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", ...FC.shorthands.gap("8px"), marginBottom: "10px" },
    tableWrap: { overflowX: "auto" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "13px" },
    cell: { textAlign: "left", whiteSpace: "nowrap", ...FC.shorthands.padding("8px"), borderBottom: `1px solid ${FC.tokens.colorNeutralStroke2}` },
    mono: { fontFamily: "Consolas, monospace" },
    muted: { color: FC.tokens.colorNeutralForeground3 },
    error: { color: FC.tokens.colorPaletteRedForeground1 },
    pager: { display: "flex", justifyContent: "flex-end", alignItems: "center", ...FC.shorthands.gap("8px"), marginTop: "10px" },
});

const download = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
};

export const UsageReport = () => {
    const styles = useStyles();
    const defaults = useMemo(initialDates, []);
    const [fromDate, setFromDate] = useState(defaults.fromDate);
    const [toDate, setToDate] = useState(defaults.toDate);
    const [tenantId, setTenantId] = useState("");
    const [tenants, setTenants] = useState([]);
    const [summary, setSummary] = useState(null);
    const [completions, setCompletions] = useState(null);
    const [rentals, setRentals] = useState(null);
    const [completionPage, setCompletionPage] = useState(1);
    const [rentalPage, setRentalPage] = useState(1);
    const [applied, setApplied] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        apiClient.getTenants().then((rows) => setTenants(rows || []))
            .catch((loadError) => setError(loadError.message || "加载租户失败"));
    }, []);

    const buildParams = useCallback(() => ({
        tenantId: Number(tenantId),
        fromUtc: dayBoundaryUtc(fromDate),
        toUtc: dayBoundaryUtc(toDate, 1),
    }), [tenantId, fromDate, toDate]);

    const runQuery = async () => {
        if (!tenantId) {
            setError("请先选择租户。");
            return;
        }
        if (!fromDate || !toDate || fromDate > toDate) {
            setError("开始日期不能晚于结束日期。");
            return;
        }
        const params = buildParams();
        setLoading(true);
        setError("");
        try {
            const [nextSummary, nextCompletions, nextRentals] = await Promise.all([
                apiClient.getUsageSummary(params),
                apiClient.getUsageCompletions({ ...params, page: 1, pageSize: 50 }),
                apiClient.getUsageRentals({ ...params, page: 1, pageSize: 50 }),
            ]);
            setSummary(nextSummary);
            setCompletions(nextCompletions);
            setRentals(nextRentals);
            setCompletionPage(1);
            setRentalPage(1);
            setApplied(params);
        } catch (queryError) {
            setError(queryError.message || "查询失败");
        } finally {
            setLoading(false);
        }
    };

    const changePage = async (kind, page) => {
        if (!applied || page < 1) return;
        setLoading(true);
        setError("");
        try {
            if (kind === "completions") {
                setCompletions(await apiClient.getUsageCompletions({ ...applied, page, pageSize: 50 }));
                setCompletionPage(page);
            } else {
                setRentals(await apiClient.getUsageRentals({ ...applied, page, pageSize: 50 }));
                setRentalPage(page);
            }
        } catch (pageError) {
            setError(pageError.message || "翻页失败");
        } finally {
            setLoading(false);
        }
    };

    const exportCsv = async (kind) => {
        if (!applied) return;
        setLoading(true);
        setError("");
        try {
            const blob = await apiClient.exportUsageCsv({ ...applied, kind });
            download(blob, `usage-${kind}-${fromDate}-${toDate}.csv`);
        } catch (exportError) {
            setError(exportError.message || "导出失败");
        } finally {
            setLoading(false);
        }
    };

    const completionPages = Math.max(1, Math.ceil((completions?.total || 0) / 50));
    const rentalPages = Math.max(1, Math.ceil((rentals?.total || 0) / 50));

    return (
        <div className={styles.root}>
            <div>
                <FC.Title2>用量报表</FC.Title2>
                <div className={styles.muted}>仅统计 Task 完成次数与设备租赁月数，不计算金额。时间窗口为本地自然日。</div>
            </div>
            <div className={styles.filters}>
                <label className={styles.field}>租户
                    <select className={styles.input} value={tenantId} onChange={(event) => setTenantId(event.target.value)}>
                        <option value="">请选择租户</option>
                        {tenants.map((item) => <option key={item.id} value={item.id}>{item.seq} · {item.name}</option>)}
                    </select>
                </label>
                <label className={styles.field}>开始日期
                    <input className={styles.input} type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
                </label>
                <label className={styles.field}>结束日期
                    <input className={styles.input} type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
                </label>
                <FC.Button appearance="primary" disabled={loading} onClick={runQuery}>{loading ? "处理中…" : "查询"}</FC.Button>
            </div>
            {error && <div className={styles.error}>{error}</div>}
            {summary && <div className={styles.cards}>
                <div className={styles.card}><FC.Body1Strong>Task 完成次数</FC.Body1Strong><div className={styles.value}>{summary.completionCount}</div><FC.Caption1>同一 Task 最多一条完成记录</FC.Caption1></div>
                <div className={styles.card}><FC.Body1Strong>设备租赁月数</FC.Body1Strong><div className={styles.value}>{summary.rentalMonthCount}</div><FC.Caption1>起租计第 1 月，每到一个滚动月锚点加 1</FC.Caption1></div>
            </div>}

            <div className={styles.section}>
                <div className={styles.sectionHeader}><div><FC.Title3>Task 完成明细</FC.Title3><div className={styles.muted}>按完成时间倒序，可追溯 Task、Session、风机、设备与证据哈希。</div></div><FC.Button disabled={!applied || loading} onClick={() => exportCsv("completions")}>导出 CSV</FC.Button></div>
                <div className={styles.tableWrap}><table className={styles.table}><thead><tr>{["完成时间", "Task", "Session", "风机", "设备", "目标", "数据哈希"].map((title) => <th key={title} className={styles.cell}>{title}</th>)}</tr></thead><tbody>
                    {(completions?.items || []).map((item) => <tr key={item.completionId}><td className={styles.cell}>{formatTime(item.acceptedAtUtc)}</td><td className={styles.cell}>{item.taskSeq} · {item.taskName}</td><td className={styles.cell}>{item.sessionSeq}</td><td className={styles.cell}>{item.turbineSeq}</td><td className={styles.cell}>{item.deviceSeq} · {item.deviceName}</td><td className={styles.cell}>{item.destination}</td><td className={`${styles.cell} ${styles.mono}`}>{item.dataHash}</td></tr>)}
                    {completions && completions.items.length === 0 && <tr><td className={styles.cell} colSpan="7">该窗口没有完成记录。</td></tr>}
                </tbody></table></div>
                <div className={styles.pager}><FC.Button disabled={loading || completionPage <= 1} onClick={() => changePage("completions", completionPage - 1)}>上一页</FC.Button><span>{completionPage} / {completionPages}</span><FC.Button disabled={loading || completionPage >= completionPages} onClick={() => changePage("completions", completionPage + 1)}>下一页</FC.Button></div>
            </div>

            <div className={styles.section}>
                <div className={styles.sectionHeader}><div><FC.Title3>设备租赁月数</FC.Title3><div className={styles.muted}>只列出窗口内存在滚动计费锚点的租期。</div></div><FC.Button disabled={!applied || loading} onClick={() => exportCsv("rentals")}>导出 CSV</FC.Button></div>
                <div className={styles.tableWrap}><table className={styles.table}><thead><tr>{["设备", "租期开始", "租期结束", "窗口内月数"].map((title) => <th key={title} className={styles.cell}>{title}</th>)}</tr></thead><tbody>
                    {(rentals?.items || []).map((item) => <tr key={item.rentalId}><td className={styles.cell}>{item.deviceSeq} · {item.deviceName}</td><td className={styles.cell}>{formatTime(item.startsAtUtc)}</td><td className={styles.cell}>{formatTime(item.endsAtUtc)}</td><td className={styles.cell}>{item.rentalMonthCount}</td></tr>)}
                    {rentals && rentals.items.length === 0 && <tr><td className={styles.cell} colSpan="4">该窗口没有租赁计费月。</td></tr>}
                </tbody></table></div>
                <div className={styles.pager}><FC.Button disabled={loading || rentalPage <= 1} onClick={() => changePage("rentals", rentalPage - 1)}>上一页</FC.Button><span>{rentalPage} / {rentalPages}</span><FC.Button disabled={loading || rentalPage >= rentalPages} onClick={() => changePage("rentals", rentalPage + 1)}>下一页</FC.Button></div>
            </div>
        </div>
    );
};
